import { Scorm2004Api, CMIBoolean, CMIElement, CMIErrorCode, ScormError } from '../api';
import { Scorm2004Schema, Model } from './schema';
import { SchemaError, ErrorCode } from './schema-engine';
import { LocalScorm2004Repository } from './local-scorm2004-repository';

enum Phase {
  notInitialized,
  initialized,
  terminated
}

export class LocalScorm2004Api implements Scorm2004Api {

  private get isBeforeInitialization() {
    return this.phase === Phase.notInitialized;
  }

  private get isInitialized() {
    return this.phase === Phase.initialized;
  }

  private get isTerminated() {
    return this.phase === Phase.terminated;
  }

  private schema: Scorm2004Schema;
  private lastError: ScormError | null = null;

  private model: Model | null = null;
  private phase: Phase = Phase.notInitialized;

  constructor(private repository: LocalScorm2004Repository) {
    this.repository = repository;
    this.schema = new Scorm2004Schema();
  }

  public Initialize(param: ''): CMIBoolean {
    this.clearLastError();

    if (this.isInitialized) {
      this.setLastError(new ScormError(CMIErrorCode.alreadyInitialized, 'Already initialized'));
      return 'false';
    }

    this.model = this.repository.load();

    const result = 'true';

    this.phase = Phase.initialized;
    return result;
  }

  public Terminate(param: ''): CMIBoolean {
    this.clearLastError();

    if (this.isBeforeInitialization) {
      this.setLastError(new ScormError(CMIErrorCode.terminationBeforeInitialization, 'Not initialized'));
      return 'false';
    }

    if (this.isTerminated) {
      this.setLastError(new ScormError(CMIErrorCode.terminationAfterTermination, 'Already terminated'));
      return 'false';
    }

    this.saveModel();

    const result = 'true';

    this.phase = Phase.terminated;
    return result;
  }

  public GetValue(element: CMIElement): string {
    this.clearLastError();

    if (this.isBeforeInitialization) {
      this.setLastError(new ScormError(CMIErrorCode.retrieveDataBeforeInitialization, 'Not initialized'));
      return '';
    }

    if (this.isTerminated) {
      this.setLastError(new ScormError(CMIErrorCode.retrieveDataAfterTermination, 'Already terminated'));
      return '';
    }

    if (!this.model) { throw new Error('Coding error: model is required here'); }

    const result = this.schema.getModel(element, this.model);
    let value = '';
    if (result.success) {
      value = result.value;
    } else {
      this.setLastError(this.mapError(result.error));
    }

    return value;
  }

  public SetValue(element: CMIElement, value: string): string {
    this.clearLastError();

    if (this.isBeforeInitialization) {
      this.setLastError(new ScormError(CMIErrorCode.storeDataBeforeInitialization, 'Not initialized'));
      return '';
    }

    if (this.isTerminated) {
      this.setLastError(new ScormError(CMIErrorCode.storeDataAfterTermination, 'Already terminated'));
      return '';
    }

    if (!this.model) { throw new Error('Coding error: model is required here'); }

    const result = this.schema.setModel(element, value, this.model);
    let resultValue: CMIBoolean;
    if (result.success) {
      this.model = result.value;
      resultValue = 'true';
    } else {
      this.setLastError(this.mapError(result.error));
      resultValue = 'false';
    }

    return resultValue;
  }

  public Commit(param: ''): CMIBoolean {
    this.clearLastError();

    if (this.isBeforeInitialization) {
      this.setLastError(new ScormError(CMIErrorCode.commitBeforeInitialization, 'Not initialized'));
      return 'false';
    }

    if (this.isTerminated) {
      this.setLastError(new ScormError(CMIErrorCode.commitAfterTermination, 'Already terminated'));
      return 'false';
    }

    this.saveModel();

    const result = 'true';

    return result;
  }

  public GetLastError(): CMIErrorCode {

    let code = CMIErrorCode.noError;
    if (this.lastError !== null) {
      code = this.lastError.code;
    }

    return code;
  }

  public GetErrorString(errorCode: CMIErrorCode): string {
    const resultValue = errorCode;

    return resultValue;
  }

  public GetDiagnostic(errorCode: CMIErrorCode): string {

    let diagnostic = '';
    if (this.lastError !== null && this.lastError.code === errorCode) {
      diagnostic = this.lastError.diagnostic;
    }

    return diagnostic;
  }

  private saveModel() {
    if (!this.model) { throw new Error('Coding error: model is required here'); }
    this.repository.save(this.model);
  }

  private setLastError(error: ScormError) {
    this.lastError = error;
  }

  private mapError(schemaError: SchemaError): ScormError {
    const code: CMIErrorCode = this.mapErrorCode(schemaError.errorCode);
    return new ScormError(code, code, schemaError.diagnosticMessage);
  }

  private mapErrorCode(code: ErrorCode): CMIErrorCode {
    switch (code) {
      case ErrorCode.success:
        return CMIErrorCode.noError;
      case ErrorCode.generalError:
        return CMIErrorCode.generalException;
      case ErrorCode.invalidArgumentError:
        return CMIErrorCode.undefinedDataModelElement;
      case ErrorCode.elementCannotHaveChildren:
        return CMIErrorCode.undefinedDataModelElement;
      case ErrorCode.elementNotAnArrayCannotHaveCount:
        return CMIErrorCode.undefinedDataModelElement;
      case ErrorCode.invalidSetElementIsKeyword:
        return CMIErrorCode.dataModelElementIsReadOnly;
      case ErrorCode.elementIsReadOnly:
        return CMIErrorCode.dataModelElementIsReadOnly;
      case ErrorCode.elementIsWriteOnly:
        return CMIErrorCode.dataModelElementIsWriteOnly;
      case ErrorCode.incorrectDataType:
        return CMIErrorCode.dataModelElementTypeMismatch;
    }
  }

  private clearLastError() {
    this.lastError = null;
  }
}
